import axios from "axios";
import { Swatch, XY } from "../types";
import { client } from "./graphql";
import { GET_CUSTOM_PRODUCT_DATA } from "./queries";
import config from "@app/config";

export const decodeShopifyId = (id: string | number, type: string) => id?.split?.(`${type}/`)?.[1] || id;

export const addToCart = async ({
  id,
  qty,
  properties = null,
}: {
  id: number | string;
  qty: string | number;
  properties?: { [key: string]: string } | null;
}) => {
  const data = {
    id: decodeShopifyId(id, "Variant"),
    quantity: qty,
  };
  if (properties) data.properties = properties;

  const res = await axios({
    method: "POST",
    url: "/cart/add.js",
    data: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "xmlhttprequest",
    },
  })
    .then((result: any) => {
      if (result.status === 200) {
        // Always increment a successful cart purchase
        document.querySelectorAll("[data-header-cart-count]")?.forEach((x) => (x.innerHTML = `${parseInt(x.innerHTML) + 1}`));
        const event = new CustomEvent("cartAdd");

        if (window.location.pathname !== "/cart") {
          window.dispatchEvent(event);
          window.refreshCart?.();
        } else {
          location.reload();
        }
      }
      return result;
    })
    .catch((error: any) => error.response.data);

  return res;
};

/**
 * Checks if string is a valid email
 * @param email string to check if valid email
 * @returns boolean
 */
export const isValidEmail = (email: string) => email?.length > 3 && /\S+@\S+\.\S+/.test(email)

export const minMax = (value: string, min: number, max: number) => !!value && value !== "" && value?.length >= min && value?.length <= max

/**
 * Fake API endpoint to retrieve custom product info
 */
export type CustomProductData = {
  variant_images: {
    [variantId: string]: string
  },
  swatches: Swatch[];
  personalisation_position: XY;
};

// returns personalisation position data, swatches and variant images. 
// optionValues determine the swatches and can also be left blank
export const getCustomProductData = async (handle: string, optionValues: string[] = []) => {
  try {
    const res = await client(GET_CUSTOM_PRODUCT_DATA(handle, optionValues));

    const variant_images = (res?.product?.variantImages?.nodes as any[])?.reduce((obj, cur) => {
      if (!cur?.image?.url) return obj
      const id = decodeShopifyId(cur.id, "ProductVariant");
      return {
        ...obj,
        [id]: cur.image.url
      }
    }, {});

    const personalisation_position = {
      x: res?.product?.personalisationLeft?.value || null,
      y: res?.product?.personalisationTop?.value || null,
    }

    const swatches = Object.keys(res)?.filter(key => key !== "product")?.map(key => {
      const fields = res[key]?.fields;
      const colour = fields?.find(f => f.key === "colour")?.value;
      const name = fields?.find(f => f.key === "name")?.value;
      const image = fields?.find(f => f.key === "image")?.reference?.image?.url;

      return { colour, name, image }
    })

    return {
      variant_images,
      personalisation_position,
      swatches,
    } as CustomProductData;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export const store = config.stores[window.Shopify.shop.replace(".myshopify.com", "")];

export const getOptionKey = (option: string, options: string[]) => {
  const optionIndex =  options?.findIndex((opt) => opt.toLowerCase() === option.toLowerCase());
  if (optionIndex === -1) return null;
  return `option${optionIndex + 1}`;
}

export const getOptionIndex = (option: string, options: string[]) => {
  return options?.findIndex((opt) => opt.toLowerCase() === option.toLowerCase());
}